<script lang="ts" setup>
import projectStore from '@/store/ProjectStore'
import FormElement from '@/components/FormElement.vue'
import formStore from '@/store/FormStore'
import userStore from '@/store/UserStore'
import { ValidatorType } from '@/helper/ValidateDirective'
import { Circuit, CircuitType, FormCircuit } from '@/helper/Types'
import { capitalizeFirstLetter } from '@/helper/tools'
import FormController from '@/controllers/FormController'
import SectionHeadline from '@/components/SectionHeadline.vue'
import ComboBox from '@/components/ComboBox.vue'
import i18n from '@/helper/Translations'

const props = withDefaults(
  defineProps<{
    validator: ValidatorType
    isCircuitsInvalid: boolean
  }>(),
  {
    isCircuitsInvalid: () => false
  }
)

defineExpose({
  validator: props.validator
})

const emit = defineEmits<{
  (e: 'validateCircuits'): void
  (e: 'flowTempChanged'): void
}>()

function systemTypeOptions(systemTypes: CircuitType[]) {
  return systemTypes.map(systemType => ({
    label: i18n.global.t(`step_heating_details.heating_system_types.${systemType}`),
    value: systemType.toString()
  }))
}

function flowTempOptions(selectedCircuit: Circuit) {
  const celsius = i18n.global.t('global.degree_celsius')
  let coolingFlowTemperature: number | undefined = undefined

  if (formStore.isCooling && selectedCircuit.cooling) {
    coolingFlowTemperature = userStore.getCoolingFlowTemperature(selectedCircuit.systemType) as number
  } else {
    coolingFlowTemperature = undefined
  }

  return getFlowTemperatures(selectedCircuit).map(flowTemp => {
    let label = `${flowTemp} ${celsius}`

    if (coolingFlowTemperature) {
      if (!formStore.isHeating) {
        label = `${coolingFlowTemperature} ${celsius}`
      } else {
        label += ` / ${coolingFlowTemperature} ${celsius}`
      }
    }

    return {
      label: label,
      value: flowTemp.toString()
    }
  })
}

function getFlowTemperatures(circuit: Circuit) {
  return userStore.getFlowTemps(circuit.systemType)
}

function editHeatingCircuit(e: Event, add: boolean, index: number | undefined = 0): void {
  if (!e || !e.currentTarget) {
    return
  }
  const circuits = formStore.data.circuits as FormCircuit[]

  if (add) {
    if (circuits.length < 3) {
      circuits.push(FormController.getEmptyFormCircuit())
    }
  } else {
    circuits.splice(index, 1)
  }

  emit('validateCircuits')
}

function circuitChanged(i: number): void {
  emit('validateCircuits')
  emit('flowTempChanged')

  const circuit = (formStore.data.circuits as FormCircuit[])[i]

  if (circuit.systemType && userStore.getSystemTypes(circuit.cooling).includes(circuit.systemType)) {
    const flowTemps = userStore.getFlowTemps(circuit.systemType)

    if (flowTemps.length === 1 || (!formStore.isHeating && formStore.isCooling)) {
      circuit.flowTemperature = flowTemps[0].toString()
    }
  }
}
</script>

<template>
  <section class="form__section has--line section--heating-system">
    <div class="form__section-inner">
      <SectionHeadline class="form__title" translation-key="step_heating_details.title_heating_system" />
      <p class="form__description">
        {{ $t('step_heating_details.info_heating_system') }}
      </p>

      <div class="form__row">
        <div :class="{ 'is--invalid': isCircuitsInvalid }" class="form__table options-table">
          <div class="tr">
            <div v-if="formStore.isCooling && formStore.isHeating" class="th column is--cooling" />
            <div class="th column is--heating-system">
              <span>{{ $t('step_heating_details.label_heating_system_types') }}</span>
            </div>
            <div class="th column is--flow-temp">
              <span v-if="!formStore.isCooling || formStore.isHeating">
                {{ $t('step_heating_details.label_flow_temperatures') }}
              </span>
            </div>
            <div class="th column is--delete" />
          </div>

          <transition-group name="fade-expand">
            <div v-for="(circuit, i) in formStore.data.circuits as FormCircuit[]" :key="circuit.id" class="tr circuit-row">
              <div v-if="formStore.isCooling && formStore.isHeating" class="td column is--cooling">
                <FormElement>
                  <label class="form__checkbox">
                    <input
                      :id="`cooling-selected-${circuit.id}`"
                      v-model="circuit.cooling"
                      type="checkbox"
                      @change="circuitChanged(i)"
                    >
                    <span class="form__cm" />
                    {{ $t('step_heating_details.label_heating_system_cooling') }}
                  </label>
                </FormElement>
              </div>

              <div class="td column is--heating-system">
                <FormElement
                  v-validate="projectStore.projectProperties.circuits.formValidationRule?.systemType"
                  :error="validator['heatingSystem' + capitalizeFirstLetter(circuit.id)]?.error"
                >
                  <ComboBox
                    :id="`heating-system-${circuit.id}`"
                    v-model="circuit.systemType"
                    :options="systemTypeOptions(userStore.getSystemTypes(circuit.cooling))"
                    @change="circuitChanged(i)"
                  />
                </FormElement>
              </div>
              <div class="td column is--flow-temp">
                <FormElement
                  v-validate="projectStore.projectProperties.circuits.formValidationRule?.flowTemperature"
                  :error="validator['flowTemp' + capitalizeFirstLetter(circuit.id)]?.error"
                >
                  <ComboBox
                    :id="`flow-temp-${circuit.id}`"
                    v-model="circuit.flowTemperature"
                    :disabled="!formStore.isHeating && formStore.isCooling"
                    :options="flowTempOptions(circuit as Circuit)"
                    @change="$emit('flowTempChanged')"
                  />
                </FormElement>
              </div>
              <div class="td column is--delete">
                <button
                  :disabled="(formStore.data.circuits as Circuit[]).length < 2"
                  class="icon-btn"
                  type="button"
                  @click="editHeatingCircuit($event, false, i)"
                >
                  <i class="ic-trash" />
                </button>
              </div>
            </div>
          </transition-group>
          <div class="tr">
            <div class="td column">
              <button
                id="btn-add-heating-system"
                :disabled="(formStore.data.circuits as Circuit[]).length > 2"
                class="btn is--tertiary has--icon-right"
                type="button"
                @click="editHeatingCircuit($event, true)"
              >
                {{ $t('step_heating_details.btn_add_circuit') }} <i class="ic-plus" />
              </button>
            </div>
          </div>
        </div>
        <div :class="{ 'is--invalid': isCircuitsInvalid }" class="form__element">
          <p v-if="isCircuitsInvalid" class="form__error">
            {{ $t('validation.circuits') }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.options-table {
  width: 100%;

  .tr {
    max-height: rem-calc(117);
    transition: all $animTimeBasic ease;
  }

  @include media('<tablet') {
    .th {
      &.is--cooling {
        display: none;
      }
    }

    .circuit-row {
      flex-wrap: wrap;
      max-height: 100%;

      .is--heating-system {
        flex-basis: 100%;
        padding: 1rem;
        border-bottom: none;
      }

      .is--flow-temp {
        flex: 1;
        padding: 0 1rem 1rem;
      }

      .is--delete {
        padding: 0;
        flex-basis: 0;
      }

      .is--cooling {
        border-bottom: none;
        padding-bottom: 0;

        label {
          margin-top: 0;
        }
      }
    }
  }

  .column {
    &.is--cooling {
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: rem-calc(160);
    }

    &.is--delete {
      flex-basis: rem-calc(60);
      min-width: rem-calc(60);
      max-width: rem-calc(60);
      flex-shrink: 0;
      flex-grow: 0;
    }
  }
}
</style>
